import React, { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api, ApiError, errorMessage } from "../api/client";
import type { Operator, Part, Role } from "../api/types";
import { Button, Field, Input, Select, Toggle } from "../components/Controls";
import { Modal } from "../components/Modal";
import { toast } from "../components/Toast";

function randomBadge() {
  const n = crypto.getRandomValues(new Uint32Array(1))[0] % 1000000;
  return `OP-${String(n).padStart(6, "0")}`;
}

export function QrModal({ code, onClose }: { code: string | null; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const print = () => {
    const w = window.open("", "_blank", "width=420,height=520");
    if (!w || !ref.current) return;
    w.document.write(`<html><head><title>${code}</title></head><body style="font-family:sans-serif;text-align:center;padding:24px">${ref.current.innerHTML}<p style="font-size:20px;font-family:monospace">${code}</p></body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };
  return (
    <Modal open={!!code} onClose={onClose} title="Código QR del gafete" testId="qr-modal" footer={<Button onClick={print} data-testid="qr-print-button">Imprimir</Button>}>
      <div className="flex flex-col items-center gap-3 py-2">
        <div ref={ref} className="rounded-lg bg-white p-3" data-testid="qr-code">{code && <QRCodeSVG value={code} size={220} includeMargin />}</div>
        <span className="font-mono text-lg" data-testid="qr-code-value">{code}</span>
      </div>
    </Modal>
  );
}

function PinModal({ op, onClose }: { op: Operator | null; onClose: () => void }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setPin("");
    setError(null);
  }, [op]);
  const save = async () => {
    if (!/^\d{4,8}$/.test(pin)) {
      setError("El PIN debe tener entre 4 y 8 dígitos.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/operators/${op.id}/reset_pin`, { pin });
      toast.success("PIN actualizado");
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open={!!op} onClose={onClose} title={`Cambiar PIN · ${op?.name || ""}`} testId="pin-modal" footer={<Button onClick={save} loading={busy} data-testid="pin-modal-save-button">Guardar</Button>}>
      <Field label="Nuevo PIN" error={error}>
        <Input type="password" inputMode="numeric" maxLength={8} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} className="font-mono tracking-[0.4em]" data-testid="pin-modal-input" />
      </Field>
    </Modal>
  );
}

export function OperatorFormModal({ open, op, parts, onClose, onSaved }: { open: boolean; op?: Operator | null; parts: Part[]; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({ name: "", username: "", pin: "", role: "operator" as Role, badge_code: "", assigned_part_id: "", active: true });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const [pinFor, setPinFor] = useState<Operator | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm({
      name: op?.name || "", username: op?.username || "", pin: "", role: op?.role || "operator",
      badge_code: op?.badge_code || "", assigned_part_id: op?.assigned_part_id || "", active: op ? op.active : true,
    });
    setErrors({});
  }, [open, op]);

  const upd = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));
  const save = async () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = "El nombre es obligatorio.";
    if (!form.username.trim()) errs.username = "El usuario es obligatorio.";
    if (!op && !/^\d{4,8}$/.test(form.pin)) errs.pin = "El PIN debe tener entre 4 y 8 dígitos.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    const body: Record<string, unknown> = {
      name: form.name.trim(), username: form.username.trim().toLowerCase(), role: form.role,
      badge_code: form.badge_code.trim() || null, assigned_part_id: form.assigned_part_id || null,
    };
    setBusy(true);
    try {
      if (op) await api.put(`/operators/${op.id}`, { ...body, active: form.active });
      else await api.post("/operators", { ...body, pin: form.pin });
      toast.success(op ? "Operario guardado" : "Operario creado");
      onSaved();
      onClose();
    } catch (e) {
      const msg = errorMessage(e);
      if (e instanceof ApiError && /gafete/i.test(msg)) setErrors({ badge_code: msg });
      else if (e instanceof ApiError && /usuario/i.test(msg)) setErrors({ username: msg });
      else if (e instanceof ApiError && /PIN/.test(msg)) setErrors({ pin: msg });
      else if (e instanceof ApiError && /pieza/i.test(msg)) setErrors({ assigned_part_id: msg });
      else toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={op ? "Editar operario" : "Nuevo operario"} testId="operator-form-modal" footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button onClick={save} loading={busy} data-testid="operator-form-save-button">Guardar</Button></>}>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Nombre" error={errors.name}><Input value={form.name} onChange={(e) => upd({ name: e.target.value })} data-testid="operator-form-name-input" /></Field>
        <Field label="Usuario" error={errors.username}><Input value={form.username} onChange={(e) => upd({ username: e.target.value })} autoCapitalize="none" className="font-mono" data-testid="operator-form-username-input" /></Field>
        {op ? (
          <Field label="PIN"><Button variant="secondary" className="w-full" onClick={() => setPinFor(op)} data-testid="operator-form-change-pin-button">Cambiar PIN</Button></Field>
        ) : (
          <Field label="PIN" error={errors.pin}><Input type="password" inputMode="numeric" maxLength={8} value={form.pin} onChange={(e) => upd({ pin: e.target.value.replace(/\D/g, "") })} className="font-mono tracking-[0.4em]" data-testid="operator-form-pin-input" /></Field>
        )}
        <Field label="Rol">
          <Select value={form.role} onChange={(e) => upd({ role: e.target.value as Role })} data-testid="operator-form-role-select">
            <option value="operator">Operario</option>
            <option value="admin">Administrador</option>
          </Select>
        </Field>
        <div className="sm:col-span-2">
          <Field label="Código de gafete" error={errors.badge_code}>
            <div className="flex gap-2">
              <Input value={form.badge_code} onChange={(e) => upd({ badge_code: e.target.value })} className="font-mono" data-testid="operator-form-badge-input" />
              <Button variant="secondary" onClick={() => upd({ badge_code: randomBadge() })} data-testid="operator-form-generate-badge-button">Generar</Button>
              <Button variant="secondary" disabled={!form.badge_code.trim()} onClick={() => setQr(form.badge_code.trim())} data-testid="operator-form-view-qr-button">Ver QR</Button>
            </div>
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Pieza asignada" error={errors.assigned_part_id}>
            <Select value={form.assigned_part_id} onChange={(e) => upd({ assigned_part_id: e.target.value })} data-testid="operator-form-part-select">
              <option value="">Sin pieza</option>
              {parts.map((p) => <option key={p.id} value={p.id}>{p.part_number} · {p.name}</option>)}
            </Select>
          </Field>
        </div>
        {op && <Toggle checked={form.active} onChange={(v) => upd({ active: v })} label="Activo" testId="operator-form-active-toggle" />}
      </div>
      <QrModal code={qr} onClose={() => setQr(null)} />
      <PinModal op={pinFor} onClose={() => setPinFor(null)} />
    </Modal>
  );
}

export { PinModal };
